/**
 * Deferred unmount — React Native's missing `<AnimatePresence>`.
 *
 * On web, framer-motion keeps an exiting subtree mounted until its exit
 * animation finishes, then removes it. React has no such thing on its own: the
 * moment a condition flips false, `{visible ? <View/> : null}` unmounts in that
 * same commit. Any exit animation started afterwards drives a value nothing is
 * attached to, so it runs invisibly and the element simply vanishes.
 *
 * This hook inverts the control: it owns a `mounted` flag that turns true
 * immediately on show, and false only in the animation's completion callback.
 * Callers gate rendering on `mounted` rather than on `visible`, and bind
 * `progress` to opacity/transform.
 *
 *   const { mounted, progress } = usePresence(isOpen, ENTER_SPRING);
 *   if (!mounted) return null;
 *   <Animated.View style={{ opacity: progress }} />
 *
 * Springs, not durations: the web widget's shells are springs, and a spring is
 * the one curve a fixed-duration `timing` cannot imitate — it settles with a
 * small overshoot rather than landing flat.
 */

import { useEffect, useRef, useState } from "react";
import { Animated } from "react-native";

export interface PresenceSpring {
  /** framer-motion's `stiffness`; RN's Animated.spring takes the same units. */
  stiffness: number;
  damping: number;
  mass?: number;
}

export interface Presence {
  /** Render while true — stays true through the exit animation. */
  mounted: boolean;
  /** 0 while hidden, 1 while shown. Drive opacity and transform with it. */
  progress: Animated.Value;
}

const DEFAULT_MASS = 1;

export const usePresence = (visible: boolean, spring: PresenceSpring): Presence => {
  const progress = useRef(new Animated.Value(0)).current;
  // Seeded false even when `visible` starts true, so a widget configured to
  // open on mount still animates in rather than appearing fully formed.
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (visible) setMounted(true);

    const animation = Animated.spring(progress, {
      toValue: visible ? 1 : 0,
      stiffness: spring.stiffness,
      damping: spring.damping,
      mass: spring.mass ?? DEFAULT_MASS,
      useNativeDriver: true,
    });

    animation.start(({ finished }) => {
      // Only a finished exit may unmount. An interrupted one means `visible`
      // flipped back mid-flight and a new animation already owns the value.
      if (finished && !visible) setMounted(false);
    });

    return () => animation.stop();
  }, [visible, progress, spring.stiffness, spring.damping, spring.mass]);

  return { mounted, progress };
};
